import { FC, useMemo } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import styles from "./Commands.module.scss";
import { InjectedCommandOption } from "./lib";
import { useCommands } from "./useCommands";

interface TaskBulkOptionsCommandsListProps {
    heading: string;
    options: InjectedCommandOption[];
    /** Runs after an option is applied. Defaults to closing the palette. */
    onOptionSelected?: () => void;
}

const OptionItem: FC<{
    readonly option: InjectedCommandOption;
    readonly onOptionSelected: () => void;
    readonly close: () => void;
}> = ({ option, onOptionSelected, close }) => (
    <CommandItem
        key={option.id}
        value={option.id}
        keywords={option.keywords}
        disabled={option.disabled}
        className={option.className}
        onSelect={() => {
            option.onSelect();
            if (option.shouldClosePalette) {
                close();
                return;
            }
            onOptionSelected();
        }}
    >
        {option.icon && (
            <span className={option.iconClassName ?? styles.optionIcon}>
                {option.icon}
            </span>
        )}
        {option.label}
        {option.sublabel && (
            <span className={styles.optionSublabel}>{option.sublabel}</span>
        )}
    </CommandItem>
);

export const TaskBulkOptionsCommandsList: FC<
    TaskBulkOptionsCommandsListProps
> = ({ heading, options, onOptionSelected }) => {
    const { close } = useCommands();
    const handleOptionSelected = onOptionSelected ?? close;

    const groups = useMemo(() => {
        const result: Array<{
            heading: string;
            items: InjectedCommandOption[];
        }> = [];
        for (const option of options) {
            if (option.groupHeader) {
                result.push({ heading: option.groupHeader, items: [option] });
            } else if (result.length > 0) {
                result[result.length - 1].items.push(option);
            } else {
                result.push({ heading, items: [option] });
            }
        }
        return result;
    }, [options, heading]);

    const hasGroupHeaders = options.some((o) => o.groupHeader);

    if (!hasGroupHeaders) {
        return (
            <Command.Group heading={heading}>
                {options.map((option) => (
                    <OptionItem
                        key={option.id}
                        option={option}
                        onOptionSelected={handleOptionSelected}
                        close={close}
                    />
                ))}
            </Command.Group>
        );
    }

    return (
        <>
            {groups.map((group, groupIndex) => (
                <Command.Group key={groupIndex} heading={group.heading}>
                    {group.items.map((option) => (
                        <OptionItem
                            key={option.id}
                            option={option}
                            onOptionSelected={handleOptionSelected}
                            close={close}
                        />
                    ))}
                </Command.Group>
            ))}
        </>
    );
};
